import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { dismissToast, showToast, ToastData, ToastKind } from "./toastSlice";
import { toast, ToastPayloadMap } from "./toastTypes";
import { BoardInviteToast } from "./toasts/BoardInviteToast";
import { EncryptionNoticeToast } from "./toasts/EncryptionNoticeToast";
import { FeatureFlagOverrideToast } from "./toasts/FeatureFlagOverrideToast";
import { NoticeToast } from "./toasts/NoticeToast";
import "./ToastManager.css";

// --- Public types ---

export type ToastProps = { dismiss: () => void }

// --- Renderer lookup (mirrors MODAL_RENDERERS) ---

type ToastRenderer<K extends ToastKind> = (payload: ToastPayloadMap[K], dismiss: () => void) => JSX.Element;

export const TOAST_RENDERERS: { [K in ToastKind]: ToastRenderer<K> } = {
  boardInvites: (p, dismiss) => <BoardInviteToast boardUid={p.boardUid} ownerUid={p.ownerUid} boardTitle={p.boardTitle} inviteCount={p.inviteCount} dismiss={dismiss} />,
  encryptionNotice: (p, dismiss) => <EncryptionNoticeToast sessionUid={p.sessionUid} boardUid={p.boardUid} dismiss={dismiss} />,
  notice: (p, dismiss) => <NoticeToast i18nKey={p.i18nKey} variant={p.variant} icon={p.icon} dismiss={dismiss} />,
  featureFlagOverrides: (p, dismiss) => <FeatureFlagOverrideToast overrides={p.overrides} dismiss={dismiss} />,
};

function renderToast(data: ToastData, dismiss: () => void): JSX.Element {
  const { kind, id: _id, ...payload } = data;
  const renderer = TOAST_RENDERERS[kind] as ToastRenderer<typeof kind>;
  return renderer(payload as ToastPayloadMap[typeof kind], dismiss);
}

// --- Animation config ---

const ENTER_DURATION = 400;
const DEFAULT_VISIBLE_DURATION = 5000;
const EXIT_DURATION = 400;

const TOAST_CONFIG: { [K in ToastKind]?: { visibleDuration?: number } } = {
  featureFlagOverrides: { visibleDuration: 15000 },
};

type AnimationPhase = "measuring" | "entering" | "visible" | "exiting";

// --- Debug support ---

const DEBUG_BOARDS = ["Alpha Board", "Beta Board", "Gamma Board", "Delta Board", "Epsilon Board"];

// --- ToastAnimationWrapper ---

function ToastAnimationWrapper({ data, isFirst }: { data: ToastData; isFirst: boolean }) {
  const dispatch = useDispatch<AppDispatch>();
  const [phase, setPhase] = useState<AnimationPhase>(isFirst ? "entering" : "measuring");
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const measureRef = useRef<HTMLDivElement>(null);

  const visibleDuration = TOAST_CONFIG[data.kind]?.visibleDuration ?? DEFAULT_VISIBLE_DURATION;

  const handleDismiss = useCallback(() => {
    dispatch(dismissToast(data.id));
  }, [dispatch, data.id]);

  useLayoutEffect(() => {
    if (phase === "measuring" && measureRef.current) {
      setMeasuredHeight(measureRef.current.offsetHeight);
      setPhase("entering");
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== "entering") return;
    const t = setTimeout(() => setPhase("visible"), ENTER_DURATION);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "visible") return;
    const t = setTimeout(() => setPhase("exiting"), visibleDuration);
    return () => clearTimeout(t);
  }, [phase, visibleDuration]);

  useEffect(() => {
    if (phase !== "exiting") return;
    const t = setTimeout(handleDismiss, EXIT_DURATION);
    return () => clearTimeout(t);
  }, [phase, handleDismiss]);

  const isMeasuring = phase === "measuring";
  const phaseClass = isMeasuring
    ? "toast-measure"
    : phase === "entering"
      ? "toast-enter"
      : phase === "exiting"
        ? "toast-exit"
        : "";

  const style: React.CSSProperties = isMeasuring
    ? { opacity: 0 }
    : { "--toast-height": `${measuredHeight}px` } as React.CSSProperties;

  return (
    <div ref={isMeasuring ? measureRef : undefined} className={`toast-item ${phaseClass}`} style={style}>
      {renderToast(data, handleDismiss)}
    </div>
  );
}

// --- ToastManager ---

export function ToastManager() {
  const dispatch = useDispatch<AppDispatch>();
  const toasts = useSelector((state: RootState) => state.toast.queue);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "T") {
        e.preventDefault();
        dispatch(showToast(toast("boardInvites", {
          boardUid: `debug-${Date.now()}`,
          ownerUid: "debug",
          boardTitle: DEBUG_BOARDS[Math.floor(Math.random() * DEBUG_BOARDS.length)],
          inviteCount: Math.ceil(Math.random() * 5),
        }, `debug-${Date.now()}`)));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dispatch]);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-stack">
      {toasts.map((t, i) => (
        <ToastAnimationWrapper key={t.id} data={t} isFirst={i === 0} />
      ))}
    </div>
  );
}
